import { MobileSearchWidget } from '@/components/search-widget/MobileSearchWidget';
import { RegularSearchWidget } from '@/components/search-widget/RegularSearchWidget';
import { cardsSelector } from '@/features/cards/cardsSelectors';
import {
  brandColorsSelector,
  salesPointConfigurationSelector,
  tenantSelector,
} from '@/features/configuration/configurationSelector';
import {
  outboundOrInboundJourneysLoadingSelector,
  purchaseFlowBookingCreationLoadingSelector,
} from '@/features/loading/loadingSelectors';
import { profileDataSelector } from '@/features/profile/profileSelectors';
import { purchaseFlowBookingIdSelector } from '@/features/purchase/purchaseSelectors';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import { useValidatedChangeBookingPromoCodes } from '@/hooks/useValidatedChangeBookingPromoCodes';
import { TransAlert } from '@/i18n/trans/alert';
import { useSelector } from '@/store/utils';
import type { BookedTripDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { buildChangeBookingSearchFormValues } from '@/utils/changeTrips';
import { isDarkBrandColor } from '@/utils/color';
import { parseUrlSearchParams } from '@/utils/urlSearchParams';
import { Divider } from '@packages/webshop-design-system';
import {
  SearchWidget,
  type SearchFormValues,
} from '@packages/webshop-search-widget';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 } from 'uuid';

function flattenFieldErrors(
  node: FieldErrors<SearchFormValues> | unknown,
  prefix = ''
): string[] {
  if (!node || typeof node !== 'object') return [];

  if (typeof (node as { message?: unknown }).message === 'string') {
    const { message } = node as { message: string };
    return [`${prefix || '(root)'}: ${message}`];
  }

  return Object.entries(node as Record<string, unknown>).flatMap(
    ([key, value]) =>
      flattenFieldErrors(value, prefix ? `${prefix}.${key}` : key)
  );
}

export const SearchWidgetWrapper = () => {
  const { pathname, search } = useLocation();
  const tenant = useSelector(tenantSelector);
  const isMaas = tenant.value === 'maas';
  const tenantConfiguration = useTenantConfiguration();
  const brandColors = useSelector(brandColorsSelector);
  const profileData = useSelector(profileDataSelector);
  const cards = useSelector(cardsSelector);
  const { handleSubmit, currentSearchFormValues, setIsSearchOverlayOpened } =
    useSearchWidget();
  const salesPointConfiguration = useSelector(salesPointConfigurationSelector);
  const purchaseBookingId = useSelector(purchaseFlowBookingIdSelector);
  const outboundOrInboundJourneysLoading = useSelector(
    outboundOrInboundJourneysLoadingSelector
  );
  const bookingLoading = useSelector(
    purchaseFlowBookingCreationLoadingSelector
  );
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const [isAutoSubmitted, setIsAutoSubmitted] = useState(false);
  const hasAutoOpenedMobileSearchRef = useRef(false);
  const searchParams = new URLSearchParams(search);
  const defaultProductType = searchParams.get('productType') as
    | 'ticket'
    | 'travelPass'
    | null;

  // When the user arrives at /purchase via the Change-Trips flow, prefill
  // the search form with the original trip's parameters: origin,
  // destination, departure date, the booking's passenger roster (locked),
  // and re-validated promo codes. Only the passenger picker is locked —
  // everything else stays editable so the customer can adjust the search.
  const changeBookingSelection = useChangeBookingSelection();
  const validatedPromoCodes = useValidatedChangeBookingPromoCodes();
  const changeBookingFormValues: SearchFormValues | undefined = useMemo(() => {
    if (
      !changeBookingSelection.hasSelection ||
      changeBookingSelection.changeFlow !== 'changeTrips'
    ) {
      return undefined;
    }
    const selectedTrips = [
      changeBookingSelection.selectedOutboundTrip,
      changeBookingSelection.selectedInboundTrip,
    ].filter((trip): trip is BookedTripDTO => trip !== null);
    const prefill = buildChangeBookingSearchFormValues(
      changeBookingSelection.selectedPassengers,
      selectedTrips,
      salesPointConfiguration?.passengerTypes,
      validatedPromoCodes
    );
    if (!prefill.passengers || prefill.passengers.length === 0)
      return undefined;
    return { ...currentSearchFormValues, ...prefill } as SearchFormValues;
  }, [
    changeBookingSelection.changeFlow,
    changeBookingSelection.hasSelection,
    changeBookingSelection.selectedPassengers,
    changeBookingSelection.selectedOutboundTrip,
    changeBookingSelection.selectedInboundTrip,
    salesPointConfiguration?.passengerTypes,
    validatedPromoCodes,
    currentSearchFormValues,
  ]);

  // If customer is logged in and has cards attached to their profile,
  // initialize empty search form values with a customer and their cards as preselected.
  const initialSearchFormValues = useMemo<SearchFormValues | undefined>(() => {
    // Skip, if form already has values, or there aren't any cards.
    if (
      changeBookingFormValues ||
      currentSearchFormValues ||
      !profileData?.cards?.length ||
      !cards?.length
    ) {
      return;
    }
    // Match customer cards with available cards.
    const profileCards = profileData.cards
      .filter(({ isActive }) => isActive)
      .map((profileCard) => {
        const card = cards.find(({ id }) => id === profileCard.cardTypeId);
        if (!card?.code) {
          return null;
        }
        return {
          issuer: card.issuer?.code,
          type: card.type?.id,
          code: card.code,
          number: profileCard.uid,
          useConcessionVouchers: false,
        };
      })
      .filter((card): card is NonNullable<typeof card> => card !== null);
    if (!profileCards.length) {
      return;
    }
    return {
      origin: null,
      destination: null,
      departureDate: new Date(),
      passengers: [
        {
          externalReference: v4(),
          type: 'PERSON',
          cards: profileCards,
        },
      ],
      productType: 'ticket',
      promoCodes: [],
    };
  }, [
    cards,
    changeBookingFormValues,
    currentSearchFormValues,
    profileData?.cards,
  ]);

  // Auto-fire the initial exchange search when entering /purchase in
  // change-booking mode. Mirrors the URL-param auto-submit branch below;
  // both share the `isAutoSubmitted` flag so they can't both fire. The
  // promo-code validation runs in parallel and lands in formValues later;
  // we don't gate the auto-submit on it (rare for the first search to
  // need them, and we'd rather show results quickly).
  useEffect(() => {
    if (isAutoSubmitted) return;
    if (!changeBookingFormValues) return;
    if (
      !changeBookingFormValues.origin?.value ||
      !changeBookingFormValues.destination?.value ||
      !changeBookingFormValues.departureDate ||
      !changeBookingFormValues.passengers?.length
    ) {
      return;
    }
    handleSubmit(changeBookingFormValues);
    setIsAutoSubmitted(true);
  }, [changeBookingFormValues, handleSubmit, isAutoSubmitted]);

  useEffect(() => {
    if (salesPointConfiguration?.currencies?.length && !purchaseBookingId) {
      const {
        productType,
        origin,
        destination,
        departureDate,
        returnDate,
        passengers,
        promoCodes,
      } = parseUrlSearchParams(salesPointConfiguration.passengerTypes);

      if (
        origin?.value &&
        destination?.value &&
        departureDate &&
        passengers?.length &&
        productType &&
        !isAutoSubmitted
      ) {
        handleSubmit({
          productType,
          origin,
          destination,
          departureDate,
          returnDate,
          passengers,
          promoCodes,
        });
      }

      setIsAutoSubmitted(true);
    }
  }, [
    handleSubmit,
    isAutoSubmitted,
    purchaseBookingId,
    salesPointConfiguration?.currencies?.length,
    salesPointConfiguration?.passengerTypes,
  ]);

  // On mobile, auto-open the search overlay when landing on /purchase with an
  // empty form (e.g. after cancelling a booking). Skip when URL params or
  // change-booking would auto-submit immediately, or when an in-progress
  // booking is being resumed (layout redirects to checkout). Once per visit —
  // closing the sheet manually won't re-trigger until the user leaves /purchase.
  useEffect(() => {
    if (pathname !== '/purchase') {
      hasAutoOpenedMobileSearchRef.current = false;
      return;
    }

    if (
      isLaptopOrBigger ||
      hasAutoOpenedMobileSearchRef.current ||
      purchaseBookingId
    ) {
      return;
    }

    const effectiveFormValues =
      changeBookingFormValues ?? currentSearchFormValues;
    if (
      effectiveFormValues?.origin?.value ||
      effectiveFormValues?.destination?.value
    ) {
      return;
    }

    if (salesPointConfiguration?.currencies?.length) {
      const { productType, origin, destination, departureDate, passengers } =
        parseUrlSearchParams(salesPointConfiguration.passengerTypes);

      if (
        origin?.value &&
        destination?.value &&
        departureDate &&
        passengers?.length &&
        productType
      ) {
        return;
      }
    }

    setIsSearchOverlayOpened(true);
    hasAutoOpenedMobileSearchRef.current = true;
  }, [
    changeBookingFormValues,
    currentSearchFormValues,
    isLaptopOrBigger,
    pathname,
    purchaseBookingId,
    salesPointConfiguration?.currencies?.length,
    salesPointConfiguration?.passengerTypes,
    setIsSearchOverlayOpened,
  ]);

  const handleValidationError = useCallback(
    (errors: FieldErrors<SearchFormValues>) => {
      const details = flattenFieldErrors(errors);
      toast.error(
        <div className="flex flex-col gap-1">
          <TransAlert i18nKey="searchFormValidationFailed" />
          {details.length > 0 && (
            <ul className="font-mono list-disc pl-4 text-xs">
              {details.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      );
    },
    []
  );

  const Component = isLaptopOrBigger ? RegularSearchWidget : MobileSearchWidget;

  // The widget colours the controls it draws straight onto the host surface
  // (promo-code trigger, product toggle, dividers) from `backgroundTheme`, so it
  // has to describe the surface it actually sits on. On desktop that is the
  // brand `bg-background` below, which is dark for some tenants; the mobile
  // sheet always paints a white panel.
  const backgroundTheme = !isLaptopOrBigger
    ? 'white'
    : isDarkBrandColor(brandColors.background)
      ? 'dark'
      : 'light';

  return (
    <div className="sticky top-0 z-50 bg-background">
      <Component>
        <SearchWidget
          tenant={tenant}
          baseUrl={import.meta.env.VITE_APP_PROXY_TARGET}
          onSubmit={handleSubmit}
          onValidationError={handleValidationError}
          height="small"
          lockedFields={{
            submitButton: outboundOrInboundJourneysLoading || bookingLoading,
            passengers: changeBookingSelection.hasSelection,
            productType: changeBookingSelection.hasSelection || isMaas,
          }}
          brandColors={brandColors}
          backgroundTheme={backgroundTheme}
          stopsSearchingStrategy={tenantConfiguration?.stopsSearchingStrategy}
          enableTimePicker={tenantConfiguration?.enableSearchWidgetTimePicker}
          formValues={
            changeBookingFormValues ??
            currentSearchFormValues ??
            initialSearchFormValues
          }
          cards={cards}
          submitOnPromoCodeChange={pathname !== '/purchase'}
          defaultProductType={defaultProductType ?? undefined}
        />
      </Component>
      <Divider variant="medium" />
    </div>
  );
};
